import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, Clock, Compass } from "lucide-react";
import { Reveal, Logo, trackEvent } from "@/components/site/Reveal";

const BOUSSOLE_URL = "https://boussole.yassineabou.fr";

const ARTICLES = [
  {
    slug: "10000-heures-reveler-son-potentiel",
    title: "Faut-il 10 000 heures pour révéler son potentiel ?",
    excerpt: "Une formule séduisante, mais aucun compteur d'heures ne garantit l'excellence. Ce qui compte : la manière de s'entraîner.",
    date: "29 octobre 2026",
    read: "3 min",
    description: "La règle des 10 000 heures est-elle vraie ? Pratique délibérée, objectif précis, retour utile : comment progresser vraiment en études, en sport ou dans un projet.",
    body: [
      { t: "p", c: "Vous avez peut-être entendu cette idée : 10 000 heures de pratique suffiraient pour devenir un expert. Popularisée par Malcolm Gladwell, cette formule est séduisante. Pourtant, aucun compteur d'heures ne garantit l'excellence." },
      { t: "p", c: "Gladwell s'appuyait sur les travaux du chercheur Anders Ericsson, qui parlait de « pratique délibérée » : un travail ciblé, attentif, corrigé. Depuis, les recherches montrent que la pratique joue un rôle important, mais qu'elle n'explique pas à elle seule les différences de performance. La manière d'apprendre mérite autant d'attention que le temps consacré." },
      { t: "h2", c: "Pratiquer avec une intention" },
      { t: "p", c: "Relire un cours sans vérifier ce que l'on retient, répéter un geste sans le corriger ou multiplier les efforts sans objectif précis : nous pouvons être très occupés sans savoir si nous progressons." },
      { t: "p", c: "Sur un tatami comme en classe, j'ai vu la même chose : une séance ciblée, avec un but clair et un retour honnête, vaut souvent mieux que trois séances subies." },
      { t: "p", c: "Une approche plus constructive consiste à :" },
      { t: "ul", c: [
        "Choisir un objectif précis : une compétence à améliorer.",
        "S'entraîner avec attention : repérer ce qui fonctionne et ce qui bloque.",
        "Obtenir un retour utile : comprendre les erreurs et ajuster sa méthode.",
        "Observer ses progrès : vérifier ce que l'on réussit désormais à faire.",
      ] },
      { t: "h2", c: "Votre prochain pas compte" },
      { t: "p", c: "Dans les études, le sport ou un projet professionnel, vous n'avez pas besoin d'attendre la millième heure pour reconnaître une avancée. Un exercice mieux compris, une décision plus claire ou une difficulté surmontée constituent déjà des progrès concrets." },
      { t: "p", c: "Chez Boussole, nous vous accompagnons pour clarifier votre cap, définir des objectifs accessibles et donner du sens à vos efforts." },
      { t: "quote", c: "Votre potentiel ne se résume pas à un nombre d'heures. Donnez une direction à votre pratique, un objectif à votre prochaine séance et une place à chaque progrès." },
    ],
  },
];

const Shell = ({ children }) => (
  <div className="min-h-screen bg-obsidian text-chalk" data-testid="ressources-page">
    <header className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
      <a href="/" className="flex items-center gap-3" aria-label="Retour à l'accueil"><Logo size={36} /><span className="font-heading font-black uppercase tracking-wide text-lg">Yassine Abou</span></a>
      <a href="/test" onClick={() => trackEvent("ressources_clic_test", { page: "ressources" })} className="hidden sm:inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/10">Faire le test gratuit</a>
    </header>
    {children}
  </div>
);

const Cta = () => (
  <div className="mt-12 rounded-2xl border border-gold/30 bg-slate2 p-6 sm:p-8" data-testid="ressources-cta">
    <p className="font-heading font-black uppercase text-xl sm:text-2xl">Et vous, où en est votre cap ?</p>
    <p className="mt-2 text-slate-300">Le test gratuit (2 minutes) vous donne un premier repère, que vous soyez collégien, lycéen, sportif ou adulte en quête de cap.</p>
    <div className="mt-5 flex flex-wrap gap-3">
      <a href="/test" onClick={() => trackEvent("ressources_clic_test", { page: "article" })} className="inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 font-semibold text-white hover:bg-emberdark transition-colors">Faire le test gratuit <ArrowRight className="h-4 w-4" /></a>
      <a href={BOUSSOLE_URL} target="_blank" rel="noreferrer" onClick={() => trackEvent("ressources_clic_boussole", { page: "article" })} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-semibold hover:bg-white/10"><Compass className="h-4 w-4" /> Découvrir Boussole</a>
    </div>
  </div>
);

const Article = ({ a }) => {
  useEffect(() => {
    document.title = `${a.title} | Yassine Abou`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", a.description);
    window.scrollTo(0, 0);
  }, [a]);
  return (
    <Shell>
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-20" data-testid="ressources-article">
        <a href="/ressources" className="text-sm text-slate-400 hover:text-white">← Toutes les ressources</a>
        <Reveal>
          <p className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] font-semibold text-gold">
            Ressources <span className="text-slate-500 normal-case tracking-normal font-normal">{a.date}</span>
            <span className="inline-flex items-center gap-1 text-slate-500 normal-case tracking-normal font-normal"><Clock className="h-3.5 w-3.5" /> {a.read}</span>
          </p>
          <h1 className="mt-4 font-heading font-black uppercase leading-[1.05] text-4xl sm:text-5xl">{a.title}</h1>
        </Reveal>
        <div className="mt-8 space-y-5 text-lg text-slate-300 leading-relaxed">
          {a.body.map((b, i) => {
            if (b.t === "h2") return <h2 key={i} className="pt-4 font-heading font-black uppercase text-2xl sm:text-3xl text-chalk">{b.c}</h2>;
            if (b.t === "ul") return (
              <ul key={i} className="space-y-3">
                {b.c.map((li) => {
                  const [head, ...rest] = li.split(" : ");
                  return <li key={li} className="flex gap-3"><span className="mt-2.5 h-2 w-2 shrink-0 rounded-full bg-ember" aria-hidden="true" /><span><strong className="text-chalk">{head}</strong>{rest.length ? " : " + rest.join(" : ") : ""}</span></li>;
                })}
              </ul>
            );
            if (b.t === "quote") return <blockquote key={i} className="border-l-4 border-ember pl-5 text-xl sm:text-2xl font-semibold text-chalk leading-snug">{b.c}</blockquote>;
            return <p key={i}>{b.c}</p>;
          })}
        </div>
        <Cta />
        <p className="mt-6 text-sm text-slate-500">Boussole est un accompagnement éducatif et personnel. Elle ne remplace ni un médecin ni un professionnel de santé.</p>
      </article>
    </Shell>
  );
};

const Liste = () => {
  useEffect(() => {
    document.title = "Ressources : méthode, objectifs et préparation mentale | Yassine Abou";
    window.scrollTo(0, 0);
  }, []);
  return (
    <Shell>
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-8 pb-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-gold">Ressources</p>
          <h1 className="mt-4 font-heading font-black uppercase leading-[1.05] text-4xl sm:text-6xl">Mieux s'entraîner, mieux avancer</h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-300">Des articles courts et concrets sur la méthode, les objectifs et la préparation mentale, pour les études, le sport et les projets de vie.</p>
        </Reveal>
        <div className="mt-10 grid gap-5">
          {ARTICLES.map((a) => (
            <a key={a.slug} href={`/ressources/${a.slug}`} data-testid={`ressources-carte-${a.slug}`} className="group block rounded-2xl border border-white/10 bg-slate2 p-6 sm:p-8 hover:border-gold/50 transition-colors">
              <p className="flex items-center gap-3 text-xs text-slate-500">{a.date} <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {a.read}</span></p>
              <h2 className="mt-2 font-heading font-black uppercase text-2xl sm:text-3xl group-hover:text-gold transition-colors">{a.title}</h2>
              <p className="mt-3 text-slate-300">{a.excerpt}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ember">Lire l'article <ArrowRight className="h-4 w-4" /></span>
            </a>
          ))}
        </div>
      </section>
    </Shell>
  );
};

const Ressources = () => {
  const { slug } = useParams();
  if (!slug) return <Liste />;
  const a = ARTICLES.find((x) => x.slug === slug);
  if (!a) return <Liste />;
  return <Article a={a} />;
};

export default Ressources;
